import axiosInstance from 'apis/axiosInstance';
import CommonButton from 'components/CommonButton/CommonButton';
import DefaultModal from 'components/Modals/DefaultModal';
import { renderToast } from 'components/Toast/Toast';
import useClinicService from 'hooks/useClinicService';
import useMatchMutate from 'hooks/useMatchMutate';
import useModal from 'hooks/useModal';
import AllowDoctorSelectionSection from 'pages/ServicesPage/AdvancedSettings/AllowtDoctorSelectionSection/AllowDoctorSelectionSection';
import BlockSchedulingToggleSection from 'pages/ServicesPage/AdvancedSettings/BlockSchedulingToggleSection/BlockSchedulingToggleSection';
import PmsScheduleToggleSection from 'pages/ServicesPage/AdvancedSettings/PmsScheduleToggleSection/PmsScheduleToggleSection';
import ExamPlacementSection from 'pages/ServicesPage/AdvancedSettings/ExamPlacementSection/ExamPlacementSection';
import OptimizeScheduleSection from 'pages/ServicesPage/AdvancedSettings/OptimizeScheduleSection/OptimizeScheduleSection';
import AvoidBookingWithinTimeFrameSection from 'pages/ServicesPage/AdvancedSettings/AvoidBookingWithinTimeFrameSection/AvoidBookingWithinTimeFrameSection';
import { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EXAM_PLACEMENT, SERVICE_NAME, SLOT_SHOW_TYPE } from 'utils/constants';
import { loadSelectedClinicId } from 'utils/storage';
import { BAD_REQUEST } from 'utils/constants/statusCode';

interface GeneralSettingModalProps {
  onClose: () => void;
  clinic: any;
}

interface FormData {
  examPlacement: string | null;
  slotShowType: string;
  block: string;
  isOptimizeSchedule: boolean;
  optimizeWeeklyDuration: number;
  isDoctorSelectionEnabled: boolean;
  isPreferredPractitionerEnabled: boolean;
  isBlockSchedulingEnabled: boolean;
  isPmsBlockSchedulingPrioritizedEnabled: boolean;
  futureAppointmentWindowDays: number | null;
}

const PMS_BLOCK_SCHEDULING_PRIORITY_SETTING_URL =
  '/clinics/pms-block-scheduling-priority-setting';

const formDataDefaultValue = {
  examPlacement: null,
  slotShowType: SLOT_SHOW_TYPE.ANY_TIME_OF_DAY,
  block: '1',
  isOptimizeSchedule: false,
  optimizeWeeklyDuration: 0,
  isDoctorSelectionEnabled: false,
  isPreferredPractitionerEnabled: false,
  isBlockSchedulingEnabled: false,
  isPmsBlockSchedulingPrioritizedEnabled: false,
  futureAppointmentWindowDays: null,
};

const GeneralSettingModal: FC<GeneralSettingModalProps> = ({
  onClose,
  clinic,
}) => {
  const { control, reset, formState, setValue, watch, handleSubmit } =
    useForm<FormData>({
      mode: 'all',
      defaultValues: formDataDefaultValue,
    });

  // Read during render so react-hook-form tracks dirty fields for onSubmit.
  const { dirtyFields } = formState;

  const { data: clinicService, mutate } = useClinicService({
    sort: 'desc,asc',
    sortBy: 'status,order',
  });

  const [isShowing, setIsShowing] = useState(false);

  const { setIsAllowed } = useModal({
    onClose,
  });

  const matchMutate = useMatchMutate();

  const checkIsDuringExamDurationInvalid = (examPlacement?: string | null) => {
    const examCleaning = clinicService?.data.find(
      (service) => service.name === SERVICE_NAME.EXAM_CLEANING
    );

    if (!examCleaning) return;

    const isDuringExamPlacement =
      EXAM_PLACEMENT.DURING_FIRST_PART_DIFF === examPlacement ||
      EXAM_PLACEMENT.DURING_LAST_PART_DIFF === examPlacement;

    if (!isDuringExamPlacement) return;

    const isSomeDoctorDurationGreaterThanHygienistDuration =
      examCleaning.childServices.some((service) =>
        service.isSplitScheduling
          ? service.doctorDuration! > service.hygienistDuration!
          : false
      );

    if (!isSomeDoctorDurationGreaterThanHygienistDuration) return;

    const examPlacementMessage =
      examPlacement === EXAM_PLACEMENT.DURING_FIRST_PART_DIFF
        ? 'During first part of hygiene appt (Different chair)'
        : 'During last part of hygiene appt (Different chair)';

    renderToast({
      type: 'error',
      message: `Settings could not be saved. Please ensure that in all Exam & Cleaning services, the doctor's duration is less than or equal to the hygienist's duration for ${examPlacementMessage} to function properly. Please review and adjust your settings accordingly.`,
    });

    return true;
  };

  const onSubmit = async (data: FormData) => {
    const payload: Partial<FormData> = {
      examPlacement: data.examPlacement,
      isDoctorSelectionEnabled: data.isDoctorSelectionEnabled,
      futureAppointmentWindowDays: data.futureAppointmentWindowDays,
    };

    const isInvalid = checkIsDuringExamDurationInvalid(payload.examPlacement);

    if (isInvalid) return;

    if (data.isOptimizeSchedule) {
      payload.isOptimizeSchedule = data.isOptimizeSchedule;
      payload.slotShowType = data.slotShowType;
      payload.block = data.block;
      payload.optimizeWeeklyDuration = data.optimizeWeeklyDuration;
    }
    const clinicId = loadSelectedClinicId();

    const promises = [];

    if (dirtyFields.isPreferredPractitionerEnabled) {
      promises.push(
        axiosInstance.patch(`/clinics/preferred-practitioner-setting`, {
          isPreferredPractitionerEnabled: data.isPreferredPractitionerEnabled,
        })
      );
    }

    if (dirtyFields.isPmsBlockSchedulingPrioritizedEnabled) {
      promises.push(
        axiosInstance.patch(PMS_BLOCK_SCHEDULING_PRIORITY_SETTING_URL, {
          isPmsBlockSchedulingPrioritizedEnabled:
            data.isPmsBlockSchedulingPrioritizedEnabled,
        })
      );
    }

    if (
      dirtyFields.examPlacement ||
      dirtyFields.isDoctorSelectionEnabled ||
      dirtyFields.isOptimizeSchedule ||
      dirtyFields.slotShowType ||
      dirtyFields.block ||
      dirtyFields.optimizeWeeklyDuration ||
      dirtyFields.futureAppointmentWindowDays
    ) {
      promises.push(
        axiosInstance.patch(`clinics/${clinicId}/services/settings`, payload)
      );
    }

    try {
      await Promise.all(promises);
      await mutate();
      matchMutate(/\/clinics/);
      reset(
        data.isOptimizeSchedule
          ? { ...data }
          : {
              ...formDataDefaultValue,
              examPlacement: data.examPlacement,
              isDoctorSelectionEnabled: data.isDoctorSelectionEnabled,
              isPreferredPractitionerEnabled:
                data.isPreferredPractitionerEnabled,
              isPmsBlockSchedulingPrioritizedEnabled:
                data.isPmsBlockSchedulingPrioritizedEnabled,
            }
      );
      setIsShowing(false);
      renderToast({
        type: 'success',
        message: 'Updated settings successfully',
      });
      onClose();
    } catch (error: any) {
      const isPmsBlockSchedulingBadRequest =
        error.response?.status === BAD_REQUEST &&
        error.config?.url === PMS_BLOCK_SCHEDULING_PRIORITY_SETTING_URL;

      if (isPmsBlockSchedulingBadRequest) {
        return renderToast({
          type: 'error',
          message: error.response?.data.message,
        });
      }

      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  const populateDefaultValue = useCallback(() => {
    if (!clinicService || !clinic) return;

    const examCleaningService = clinicService?.data.find(
      (service) => service.name === SERVICE_NAME.EXAM_CLEANING
    );

    if (!examCleaningService) return;

    reset({
      examPlacement: examCleaningService.examPlacement,
      slotShowType:
        examCleaningService.slotShowType || formDataDefaultValue.slotShowType,
      block: examCleaningService.block || formDataDefaultValue.block,
      isOptimizeSchedule:
        examCleaningService.isOptimizeSchedule ||
        formDataDefaultValue.isOptimizeSchedule,
      optimizeWeeklyDuration: examCleaningService.optimizeWeeklyDuration || 0,
      isDoctorSelectionEnabled:
        clinic.isDoctorSelectionEnabled ||
        formDataDefaultValue.isDoctorSelectionEnabled,
      isPreferredPractitionerEnabled:
        clinic.isPreferredPractitionerEnabled ||
        formDataDefaultValue.isPreferredPractitionerEnabled,
      isBlockSchedulingEnabled: clinic.isBlockSchedulingEnabled || false,
      isPmsBlockSchedulingPrioritizedEnabled:
        clinic.isPmsBlockSchedulingPrioritizedEnabled || false,
      futureAppointmentWindowDays:
        clinic.futureAppointmentWindowDays ??
        formDataDefaultValue.futureAppointmentWindowDays,
    });
  }, [clinic, clinicService, reset]);

  useEffect(() => {
    populateDefaultValue();
  }, [populateDefaultValue]);

  return (
    <DefaultModal>
      <>
        <div className="w-[50rem] overflow-auto max-h-[80vh] pb-[10px]">
          <p className="text-20 leading-[3rem] font-bold pt-1.6 pb-1.2 pl-3 border-b border-light-grey">
            General Service Settings
          </p>
          <div className="px-3 mt-2.4">
            <div className="flex flex-col gap-y-2.8 -mx-0.8">
              <BlockSchedulingToggleSection
                control={control}
                setIsAllowed={setIsAllowed}
              />
              <PmsScheduleToggleSection control={control} />
              <AvoidBookingWithinTimeFrameSection
                control={control}
                setValue={setValue}
              />
              <ExamPlacementSection control={control} />
              <AllowDoctorSelectionSection
                control={control}
                setValue={setValue}
                isPreferredPractitionerEnabled={watch(
                  'isPreferredPractitionerEnabled'
                )}
              />
              <OptimizeScheduleSection control={control} />
            </div>
          </div>
        </div>

        <div className="px-3 mt-2.4 pb-3.2 flex gap-x-1.6 justify-end">
          <CommonButton variant="secondary" onClick={onClose}>
            Cancel
          </CommonButton>
          <CommonButton onClick={handleSubmit(onSubmit)} isLoading={isShowing}>
            Save
          </CommonButton>
        </div>
      </>
    </DefaultModal>
  );
};

export default GeneralSettingModal;
